/**
 * TD - Footer overlay entry points.
 *
 * Relocates the site's on-load overlays to on-demand footer links: the theme's own
 * email/SMS capture popup, Consentmo's cookie-consent banner, and Consentmo's ADA
 * accessibility widget. Each link stays hidden until its target is proven present, so a
 * disabled popup or an uninstalled app never leaves a dead link in the footer.
 *
 * The two Consentmo widgets are app-injected custom elements (csm-cookie-consent,
 * csm-web-accessibility) and the app publishes no documented JS API in this theme. Two
 * ways to reach them, tried in order:
 *
 *   1. A global function name configured on the block. If Consentmo support supplies one,
 *      that is authoritative and is used verbatim.
 *   2. A DOM bridge — the widgets keep their collapsed re-open toggle inside an OPEN
 *      shadow root, so we find that toggle and click it. This is the same shadow-root
 *      access assets/td-floating-widgets.js already relies on.
 *
 * The bridge deliberately refuses to click while a widget's full panel is on screen, so a
 * footer click can never land on a consent panel's "Accept" control.
 *
 * Rendered by blocks/td-footer-overlay-links.liquid.
 */
(function () {
  if (customElements.get('td-overlay-links')) return;

  var POPUP_OPEN_EVENT = 'td:popup-email-capture:open';

  // Light-DOM host for each target. The popup is ours; the csm-* elements are Consentmo's.
  var HOSTS = {
    popup: 'td-popup-email-capture',
    consent: 'csm-cookie-consent',
    accessibility: 'csm-web-accessibility',
  };

  // Root classes that park a floating widget once its footer link can reach it.
  var HIDE_CLASS = {
    consent: 'td-overlays--hide-consent',
    accessibility: 'td-overlays--hide-accessibility',
  };

  // Consentmo records the visitor's answer here. Its presence means consent has already
  // been collected, which is the only state where hiding the floating toggle is safe.
  var CONSENT_CACHE_KEY = 'gdprCache';

  // App embeds load async, so targets are watched rather than read once. Watching stops
  // after this long so a store without the apps is not observed forever.
  var WATCH_TIMEOUT_MS = 15000;

  // Consent is usually answered long after the links resolve, and Consentmo records the
  // answer in localStorage — no event, and the banner's own DOM sits in a shadow root the
  // MutationObserver cannot see. So a pending consent park gets its own bounded poll
  // instead of riding on watch(), which stops as soon as the links are reachable.
  var CONSENT_POLL_MS = 1000;
  var CONSENT_POLL_TIMEOUT_MS = 300000;

  // A collapsed re-open toggle sits near a screen edge; a full panel does not. Shared with
  // the sizing heuristic below to tell the two states apart.
  var TOGGLE_ANCHOR_MAX_PX = 400;
  var PANEL_MIN_HEIGHT_PX = 200;

  /**
   * Resolves a dotted global path, e.g. "isenseRules.showSettings".
   *
   * @param {string} path
   * @returns {Function|null}
   */
  function globalFunction(path) {
    if (!path) return null;

    var target = window;
    var parts = String(path).trim().split('.');
    for (var i = 0; i < parts.length; i++) {
      if (target === null || typeof target === 'undefined') return null;
      target = target[parts[i]];
    }

    return typeof target === 'function' ? target : null;
  }

  /**
   * True when a control is a collapsed re-open toggle rather than ordinary panel content:
   * positioned, and anchored near a screen edge.
   *
   * @param {Element} node
   * @returns {boolean}
   */
  function isEdgeAnchored(node) {
    // The toggle is often an inner button inside a fixed wrapper, and both Consentmo
    // widgets position that wrapper from a class, not an inline style. So read computed
    // positions up the shadow tree and measure the first fixed or absolute element.
    // parentElement is null at the shadow root, which ends the walk.
    var anchor = node;
    var style = getComputedStyle(anchor);
    while (style.position !== 'fixed' && style.position !== 'absolute') {
      anchor = anchor.parentElement;
      if (!anchor) return false;
      style = getComputedStyle(anchor);
    }

    var bottom = parseFloat(style.bottom);
    var top = parseFloat(style.top);
    return (
      (isFinite(bottom) && bottom <= TOGGLE_ANCHOR_MAX_PX) ||
      (isFinite(top) && top <= TOGGLE_ANCHOR_MAX_PX)
    );
  }

  /**
   * One walk of the widget's open shadow root answering both questions a footer click
   * needs: is the full panel already on screen, and where is the collapsed re-open toggle.
   *
   * Both answers come from offset reads, which force layout, so they share a single pass
   * rather than reflowing twice per click. A visible panel short-circuits the walk — the
   * toggle is irrelevant once there is nothing to re-open.
   *
   * @param {Element} host
   * @returns {{panelOpen: boolean, toggle: Element|null}}
   */
  function inspectWidget(host) {
    var nodes = host.shadowRoot.querySelectorAll('*');
    var panelOpen = false;
    var toggle = null;

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (!node.offsetWidth || !node.offsetHeight) continue;

      // A full panel is tall; a collapsed toggle is not.
      if (node.offsetHeight >= PANEL_MIN_HEIGHT_PX) {
        panelOpen = true;
        break;
      }

      if (toggle || !node.matches('button, a, [role="button"]')) continue;
      if (isEdgeAnchored(node)) toggle = node;
    }

    return { panelOpen: panelOpen, toggle: toggle };
  }

  /**
   * @returns {boolean} True once Consentmo has stored a consent answer.
   */
  function consentRecorded() {
    try {
      var cache = localStorage.getItem(CONSENT_CACHE_KEY);
      if (!cache) return false;
      return typeof JSON.parse(cache).updatedPreferences !== 'undefined';
    } catch (error) {
      // Unparseable or unavailable storage — treat consent as not yet given, which is
      // the conservative answer: the banner stays visible.
      return false;
    }
  }

  class TdOverlayLinks extends HTMLElement {
    connectedCallback() {
      this.globals = {
        consent: this.dataset.consentGlobal || '',
        accessibility: this.dataset.accessibilityGlobal || '',
      };
      this.hideWidget = {
        consent: this.dataset.hideConsent === 'true',
        accessibility: this.dataset.hideAccessibility === 'true',
      };

      // Widgets the visitor has opened from the footer. Once opened, a widget is never
      // re-parked from under them by a later resolve pass.
      this.unparked = {};

      this.triggers = {};
      var buttons = this.querySelectorAll('[data-td-overlay-trigger]');
      for (var i = 0; i < buttons.length; i++) {
        var key = buttons[i].dataset.tdOverlayTrigger;
        this.triggers[key] = buttons[i];
        buttons[i].addEventListener('click', this.handleClick.bind(this, key));
      }

      this.resolve();
      this.watch();
      this.watchConsent();
    }

    disconnectedCallback() {
      this.stopWatch();
      this.stopConsentWatch();
    }

    /* --- availability --- */

    /**
     * Reveals each link whose target is reachable, and parks the floating widget when the
     * merchant has opted into that. Safe to call repeatedly as app embeds arrive.
     */
    resolve() {
      var resolved = 0;

      Object.keys(this.triggers).forEach((key) => {
        var available = key === 'popup' ? this.popupAvailable() : this.widgetAvailable(key);

        this.triggers[key].hidden = !available;
        if (!available) return;

        resolved++;
        if (key !== 'popup') this.parkWidget(key);
      });

      this.hidden = resolved === 0;
      return resolved === Object.keys(this.triggers).length;
    }

    /**
     * @returns {boolean} True when the popup section is rendered and enabled.
     */
    popupAvailable() {
      return Boolean(document.querySelector(HOSTS.popup));
    }

    /**
     * @param {string} key - 'consent' or 'accessibility'.
     * @returns {boolean}
     */
    widgetAvailable(key) {
      if (globalFunction(this.globals[key])) return true;

      var host = document.querySelector(HOSTS[key]);
      return Boolean(host && host.shadowRoot);
    }

    /**
     * Hides a floating widget now that the footer carries the same entry point. Consent is
     * only ever parked after the visitor has answered the banner — the first-visit banner
     * must always be free to show itself.
     *
     * @param {string} key
     */
    parkWidget(key) {
      if (!this.hideWidget[key] || this.unparked[key]) return;
      if (key === 'consent' && !consentRecorded()) return;

      document.documentElement.classList.add(HIDE_CLASS[key]);
    }

    /* --- watching --- */

    // App embeds and the popup section can mount after this block, so targets are watched
    // until all links resolve or the timeout expires.
    watch() {
      if (this.observer) return;

      // Debounced to one frame: app embeds mount noisily and resolve() only needs to run
      // once per batch of DOM changes.
      this.observer = new MutationObserver(() => {
        if (this.watchPending) return;
        this.watchPending = true;
        this.watchFrame = requestAnimationFrame(() => {
          this.watchPending = false;
          if (this.resolve()) this.stopWatch();
        });
      });
      this.observer.observe(document.body, { childList: true, subtree: true });

      this.watchTimer = setTimeout(() => this.stopWatch(), WATCH_TIMEOUT_MS);
    }

    stopWatch() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.watchFrame) {
        cancelAnimationFrame(this.watchFrame);
        this.watchFrame = null;
      }
      this.watchPending = false;
      clearTimeout(this.watchTimer);
    }

    /**
     * Parks the consent toggle once the visitor answers the banner, which happens well
     * after watch() has stopped. Only runs when the merchant opted into parking and a
     * consent link exists to reach the widget by.
     */
    watchConsent() {
      if (this.consentTimer || !this.hideWidget.consent || !this.triggers.consent) return;

      var started = Date.now();
      this.consentTimer = setInterval(() => {
        if (Date.now() - started > CONSENT_POLL_TIMEOUT_MS || this.unparked.consent) {
          this.stopConsentWatch();
          return;
        }

        // The link resolving is what makes parking safe: it is the way back to the widget.
        if (this.triggers.consent.hidden || !consentRecorded()) return;

        this.parkWidget('consent');
        this.stopConsentWatch();
      }, CONSENT_POLL_MS);
    }

    stopConsentWatch() {
      clearInterval(this.consentTimer);
      this.consentTimer = null;
    }

    /* --- opening --- */

    /**
     * @param {string} key
     * @param {MouseEvent} event
     */
    handleClick(key, event) {
      event.preventDefault();

      if (key === 'popup') {
        document.dispatchEvent(new CustomEvent(POPUP_OPEN_EVENT));
        return;
      }

      this.openWidget(key);
    }

    /**
     * @param {string} key - 'consent' or 'accessibility'.
     */
    openWidget(key) {
      // A configured global wins: if Consentmo support supplied one it is authoritative.
      var open = globalFunction(this.globals[key]);
      if (open) {
        open.call(window);
        return;
      }

      var host = document.querySelector(HOSTS[key]);
      if (!host || !host.shadowRoot) return;

      // Un-park first so the widget has somewhere to appear, and keep it un-parked.
      this.unparked[key] = true;
      document.documentElement.classList.remove(HIDE_CLASS[key]);

      // Un-parking changes the widget's layout, so give it a frame to settle before
      // measuring it. Reading in the same frame can see a still-hidden toggle and send
      // the click somewhere the visitor cannot see.
      requestAnimationFrame(() => {
        var state = inspectWidget(host);
        if (state.panelOpen || !state.toggle) return;

        state.toggle.click();
      });
    }
  }

  customElements.define('td-overlay-links', TdOverlayLinks);
})();
